<script lang="ts">
    export interface Props {
        text: string | null;
    }

    let { text }: Props = $props();

    type HintSegment =
        | { kind: "text"; text: string }
        | { kind: "region"; region: number };

    const REGION_COLORS = [
        "red",
        "cyan",
        "yellow",
        "lime",
        "grey",
        "sky",
        "green",
        "pink",
        "orange",
        "indigo",
    ] as const;

    const normalizeRegionPlaceholders = (raw: string): string => {
        let result = raw.replace(/\b[Rr]egions\s+([0-9,\s]+)/g, (match, group) => {
            const parts = String(group)
                .split(",")
                .map((part) => part.trim())
                .filter((part) => part.length > 0);
            if (parts.length === 0) return match;
            const tokens = parts.map((part) => `{{region-${part}}}`).join(", ");
            return `regions ${tokens}`;
        });

        result = result.replace(/\b[Rr]egion\s+(\d+)/g, (_match, num) => `{{region-${num}}}`);
        return result;
    };

    const parseHintText = (value: string | null): HintSegment[] => {
        if (!value) return [];

        const normalized = normalizeRegionPlaceholders(value);
        const segments: HintSegment[] = [];
        const regex = /\{\{region-(\d+)\}\}/g;

        let lastIndex = 0;
        let match: RegExpExecArray | null;

        while ((match = regex.exec(normalized)) !== null) {
            if (match.index > lastIndex) {
                segments.push({
                    kind: "text",
                    text: normalized.slice(lastIndex, match.index),
                });
            }

            const regionNumber = parseInt(match[1], 10);
            if (!Number.isNaN(regionNumber)) {
                segments.push({ kind: "region", region: regionNumber });
            }

            lastIndex = match.index + match[0].length;
        }

        if (lastIndex < normalized.length) {
            segments.push({
                kind: "text",
                text: normalized.slice(lastIndex),
            });
        }

        return segments;
    };

    const regionColorVar = (regionNumber: number): string | null => {
        const index = regionNumber - 1;
        if (index < 0 || index >= REGION_COLORS.length) return null;
        return `--cell-${REGION_COLORS[index]}`;
    };

    const regionColorName = (regionNumber: number): string => {
        const index = regionNumber - 1;
        if (index < 0 || index >= REGION_COLORS.length) return "";
        const color = REGION_COLORS[index];
        return color.charAt(0).toUpperCase() + color.slice(1);
    };
</script>

{#if text}
    <div class="hint-panel">
        <p>
            <b>HINT:</b>
            {#each parseHintText(text) as segment}
                {#if segment.kind === "text"}
                    {segment.text}
                {:else}
                    the 
                    <span
                        class="hint-region-label"
                        style={regionColorVar(segment.region)
                            ? `background-color: var(${regionColorVar(segment.region)}); color: rgba(0, 0, 0, 0.6);`
                            : ""}
                    >
                        {regionColorName(segment.region)} region
                    </span>
                {/if}
            {/each}
        </p>
    </div>
{/if}
